/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Last Path Service - Remembers the last page the user visited
 *
 * This allows redirecting users back to where they were after login.
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

const STORAGE_KEY = 'app_last_path'

// Paths that should NOT be saved (auth-related, error pages, etc.)
const EXCLUDED_PATHS = [
  '/login',
  '/signup',
  '/register',
  '/register/student',
  '/register/guardian',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/verify',
  '/logout',
  '/confirm-deletion',
  '/legal/terms',
  '/legal/privacy',
  '/404',
  '/error'
]

// Paths that should be excluded by prefix
const EXCLUDED_PREFIXES = ['/auth/', '/oauth/']

/**
 * Check if a path should be saved
 */
function shouldSavePath(path) {
  if (!path) return false

  // Normalize path
  const normalizedPath = path.split('?')[0] // Remove query string

  // Check exact matches
  if (EXCLUDED_PATHS.includes(normalizedPath)) {
    return false
  }

  // Check prefix matches
  for (const prefix of EXCLUDED_PREFIXES) {
    if (normalizedPath.startsWith(prefix)) {
      return false
    }
  }

  return true
}

/**
 * Save the current path to localStorage
 * @param {string} path - The path to save
 */
export function saveLastPath(path) {
  if (shouldSavePath(path)) {
    localStorage.setItem(STORAGE_KEY, path)
  }
}

/**
 * Get the last saved path
 * @returns {string|null} The last path or null if none saved
 */
export function getLastPath() {
  return localStorage.getItem(STORAGE_KEY)
}

/**
 * Clear the last saved path
 */
export function clearLastPath() {
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * Get and clear the last path in one operation
 * Useful for redirect-after-login scenarios
 * @returns {string|null} The last path or null if none saved
 */
export function consumeLastPath() {
  const path = getLastPath()
  clearLastPath()
  return path
}

export default {
  save: saveLastPath,
  get: getLastPath,
  clear: clearLastPath,
  consume: consumeLastPath
}
